/* ============================================================================
 * Project        : Deloitte Digital Compliance
 * File           : MindMap.jsx
 * Version        : 1.0.0
 * Author         : Team Neubinary
 * Created On     : 2026-06-24
 *
 * Description    : Digital Compliance for Automation and Governance
 *
 * Change History :
 * ----------------------------------------------------------------------------
 * Date         | Version | Author           | Description
 * ----------------------------------------------------------------------------
 * 2026-06-24   | 1.0.0   | Team Neubinary   | Initial creation
 * <YYYY-MM-DD> | x.x.x   | <Name/Team>      | <Change description>
 * ============================================================================
 *
 * Copyright (c) 2026 AU79 Consulting Services Pvt. Ltd.
 * All Rights Reserved.
 *
 * This source code and its contents are confidential and proprietary to
 * AU79 Consulting Services Pvt. Ltd. Unauthorized copying, reproduction,
 * modification, distribution, or disclosure of this material, in whole or
 * in part, is strictly prohibited without prior written permission from
 * AU79 Consulting Services Pvt. Ltd.
 * ============================================================================
 */
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import PropTypes from "prop-types";
import {
  ReactFlow,
  Controls,
  Background,
  ControlButton,
  applyEdgeChanges,
  applyNodeChanges,
  addEdge,
  MarkerType,
  BaseEdge,
  useInternalNode,
  useNodes,
} from "@xyflow/react";
import "@xyflow/react/dist/style.css";
import * as d3 from "d3-hierarchy";
import { Box } from "@mui/material";
import RestartAltIcon from "@mui/icons-material/RestartAlt";
import { shallowEqual, useDispatch, useSelector } from "react-redux";
import { HandleApiActions, labCar } from "../../redux/actions";
import customConsole from "../../config/customConsole";

const CLAMPED_TEXT_BASE_STYLE = {
  textAlign: "center",
  lineHeight: "12px",
  display: "-webkit-box",
  WebkitBoxOrient: "vertical",
  overflow: "hidden",
  textOverflow: "ellipsis",
  wordBreak: "break-word",
  overflowWrap: "anywhere",
};

const nodeWidth = 140;
const nodeHeight = 140;
const marginX = 50;
const marginY = 50;

const segmentHitsBox = (p1, p2, box, padding = 4) => {
  const bMinX = box.x - padding;
  const bMaxX = box.x + box.w + padding;
  const bMinY = box.y - padding;
  const bMaxY = box.y + box.h + padding;

  if (Math.abs(p1.y - p2.y) < 1) {
    const y = p1.y;
    if (y < bMinY || y > bMaxY) return false;
    const segMinX = Math.min(p1.x, p2.x);
    const segMaxX = Math.max(p1.x, p2.x);
    return segMaxX > bMinX && segMinX < bMaxX;
  }

  if (Math.abs(p1.x - p2.x) < 1) {
    const x = p1.x;
    if (x < bMinX || x > bMaxX) return false;
    const segMinY = Math.min(p1.y, p2.y);
    const segMaxY = Math.max(p1.y, p2.y);
    return segMaxY > bMinY && segMinY < bMaxY;
  }

  const minX = Math.min(p1.x, p2.x);
  const maxX = Math.max(p1.x, p2.x);
  const minY = Math.min(p1.y, p2.y);
  const maxY = Math.max(p1.y, p2.y);
  if (maxX < bMinX || minX > bMaxX || maxY < bMinY || minY > bMaxY) return false;
  return true;
};

const segmentPenetratesBox = (p1, p2, box, padding = 2) => {
  const bMinX = box.x + padding;
  const bMaxX = box.x + box.w - padding;
  const bMinY = box.y + padding;
  const bMaxY = box.y + box.h - padding;

  if (bMaxX <= bMinX || bMaxY <= bMinY) return false;

  if (Math.abs(p1.y - p2.y) < 1) {
    const y = p1.y;
    if (y < bMinY || y > bMaxY) return false;
    const segMinX = Math.min(p1.x, p2.x);
    const segMaxX = Math.max(p1.x, p2.x);
    return segMaxX > bMinX && segMinX < bMaxX;
  }

  if (Math.abs(p1.x - p2.x) < 1) {
    const x = p1.x;
    if (x < bMinX || x > bMaxX) return false;
    const segMinY = Math.min(p1.y, p2.y);
    const segMaxY = Math.max(p1.y, p2.y);
    return segMaxY > bMinY && segMinY < bMaxY;
  }

  return false;
};

const countCollisions = (points, obstacles, srcBox, tgtBox, padding = 4) => {
  let count = 0;
  for (let i = 0; i < points.length - 1; i++) {
    const p1 = points[i];
    const p2 = points[i + 1];

    for (const obs of obstacles) {
      if (segmentHitsBox(p1, p2, obs, padding)) {
        count++;
      }
    }

    if (srcBox && segmentPenetratesBox(p1, p2, srcBox, 2)) {
      count += 10;
    }
    if (tgtBox && segmentPenetratesBox(p1, p2, tgtBox, 2)) {
      count += 10;
    }
  }
  return count;
};

const pathLength = (points) => {
  let len = 0;
  for (let i = 0; i < points.length - 1; i++) {
    len += Math.hypot(points[i + 1].x - points[i].x, points[i + 1].y - points[i].y);
  }
  return len;
};

const cleanPoints = (pts) => {
  if (pts.length < 2) return pts;
  const result = [pts[0]];
  for (let i = 1; i < pts.length; i++) {
    const prev = result[result.length - 1];
    const curr = pts[i];
    if (Math.abs(prev.x - curr.x) < 1 && Math.abs(prev.y - curr.y) < 1) continue;
    result.push(curr);
  }

  const simplified = [result[0]];
  for (let i = 1; i < result.length - 1; i++) {
    const prev = simplified[simplified.length - 1];
    const curr = result[i];
    const next = result[i + 1];

    const dx1 = curr.x - prev.x;
    const dy1 = curr.y - prev.y;
    const dx2 = next.x - curr.x;
    const dy2 = next.y - curr.y;

    if (Math.abs(dy1) < 1 && Math.abs(dy2) < 1) continue;
    if (Math.abs(dx1) < 1 && Math.abs(dx2) < 1) continue;

    simplified.push(curr);
  }
  simplified.push(result[result.length - 1]);
  return simplified;
};

const buildSmoothOrthogonalPath = (points, radius = 16) => {
  const pts = cleanPoints(points);
  if (pts.length < 2) return "";
  if (pts.length === 2) {
    return `M ${pts[0].x} ${pts[0].y} L ${pts[1].x} ${pts[1].y}`;
  }

  let d = `M ${pts[0].x} ${pts[0].y}`;

  for (let i = 1; i < pts.length - 1; i++) {
    const pPrev = pts[i - 1];
    const pCurr = pts[i];
    const pNext = pts[i + 1];

    const d1 = { x: pCurr.x - pPrev.x, y: pCurr.y - pPrev.y };
    const len1 = Math.hypot(d1.x, d1.y);
    const d2 = { x: pNext.x - pCurr.x, y: pNext.y - pCurr.y };
    const len2 = Math.hypot(d2.x, d2.y);

    if (len1 === 0 || len2 === 0) continue;

    const r = Math.min(radius, len1 / 2, len2 / 2);

    const startX = pCurr.x - (d1.x / len1) * r;
    const startY = pCurr.y - (d1.y / len1) * r;
    const endX = pCurr.x + (d2.x / len2) * r;
    const endY = pCurr.y + (d2.y / len2) * r;

    d += ` L ${startX} ${startY} Q ${pCurr.x} ${pCurr.y} ${endX} ${endY}`;
  }

  const pLast = pts[pts.length - 1];
  d += ` L ${pLast.x} ${pLast.y}`;
  return d;
};

/**
 * Calculates an obstacle-avoiding routing edge path that evaluates all candidate
 * paths against intermediate software modules, EE components, and tree branches,
 * guaranteed to select a path with zero collisions with other software modules.
 */
const getPerimeterEdgePath = (
  sourceNode,
  targetNode,
  allNodes = [],
  linkIndex = 0,
  totalLinks = 1,
) => {
  if (!sourceNode || !targetNode) return "";

  let minX = Infinity;
  let maxX = -Infinity;
  let minY = Infinity;
  let maxY = -Infinity;
  const obstacles = [];

  for (const n of allNodes) {
    const pos =
      n.internals?.positionAbsolute || n.position || { x: 0, y: 0 };
    const w = n.measured?.width || n.style?.width || nodeWidth;
    const h = n.measured?.height || n.style?.height || nodeHeight;
    const wNum = typeof w === "number" ? w : nodeWidth;
    const hNum = typeof h === "number" ? h : nodeHeight;

    minX = Math.min(minX, pos.x);
    maxX = Math.max(maxX, pos.x + wNum);
    minY = Math.min(minY, pos.y);
    maxY = Math.max(maxY, pos.y + hNum);

    if (n.id !== sourceNode.id && n.id !== targetNode.id) {
      obstacles.push({ id: n.id, x: pos.x, y: pos.y, w: wNum, h: hNum });
    }
  }

  if (!Number.isFinite(minX)) {
    minX = -600;
    maxX = 600;
    minY = -600;
    maxY = 600;
  }

  // Dedicated clearance lane outside the entire diagram, compact and staggered per link
  const clearance = 20 + linkIndex * 10;
  const leftLane = minX - clearance;
  const rightLane = maxX + clearance;
  const topLane = minY - clearance;
  const bottomLane = maxY + clearance;

  const wA =
    sourceNode.measured?.width ||
    (typeof sourceNode.style?.width === "number"
      ? sourceNode.style.width
      : nodeWidth);
  const hA =
    sourceNode.measured?.height ||
    (typeof sourceNode.style?.height === "number"
      ? sourceNode.style.height
      : nodeHeight);
  const wB =
    targetNode.measured?.width ||
    (typeof targetNode.style?.width === "number"
      ? targetNode.style.width
      : nodeWidth);
  const hB =
    targetNode.measured?.height ||
    (typeof targetNode.style?.height === "number"
      ? targetNode.style.height
      : nodeHeight);

  const posA =
    sourceNode.internals?.positionAbsolute || sourceNode.position || { x: 0, y: 0 };
  const posB =
    targetNode.internals?.positionAbsolute || targetNode.position || { x: 0, y: 0 };

  const cA = { x: posA.x + wA / 2, y: posA.y + hA / 2 };
  const cB = { x: posB.x + wB / 2, y: posB.y + hB / 2 };

  const srcBox = { x: posA.x, y: posA.y, w: wA, h: hA };
  const tgtBox = { x: posB.x, y: posB.y, w: wB, h: hB };

  const maxAnchorSpread = 16;
  const stepSpread = totalLinks > 1 ? maxAnchorSpread / (totalLinks - 1) : 0;
  const anchorOffset =
    totalLinks > 1 ? -maxAnchorSpread / 2 + linkIndex * stepSpread : 0;

  const dx = cB.x - cA.x;
  const dy = cB.y - cA.y;
  const isHorizontalSeparation = Math.abs(dx) >= Math.abs(dy);
  const offset = 16 + linkIndex * 8;

  const rawCandidates = [];

  // 1. Local direction-aware bypasses (clean facing connect or arch around)
  if (isHorizontalSeparation) {
    if (Math.abs(cA.y - cB.y) < 15) {
      if (dx > 0) {
        rawCandidates.push([
          { x: posA.x + wA, y: cA.y + anchorOffset },
          { x: posB.x, y: cA.y + anchorOffset },
        ]);
      } else {
        rawCandidates.push([
          { x: posA.x, y: cA.y + anchorOffset },
          { x: posB.x + wB, y: cA.y + anchorOffset },
        ]);
      }
    }

    if (dx > 0) {
      // Node A is on the left, Node B is on the right
      const midX = posA.x + wA + (posB.x - (posA.x + wA)) / 2;
      rawCandidates.push([
        { x: posA.x + wA, y: cA.y + anchorOffset },
        { x: midX, y: cA.y + anchorOffset },
        { x: midX, y: cB.y + anchorOffset * 0.5 },
        { x: posB.x, y: cB.y + anchorOffset * 0.5 },
      ]);
    } else {
      // Node B is on the left, Node A is on the right
      const midX = posB.x + wB + (posA.x - (posB.x + wB)) / 2;
      rawCandidates.push([
        { x: posA.x, y: cA.y + anchorOffset },
        { x: midX, y: cA.y + anchorOffset },
        { x: midX, y: cB.y + anchorOffset * 0.5 },
        { x: posB.x + wB, y: cB.y + anchorOffset * 0.5 },
      ]);
    }

    // Top arch bypass (above both horizontal nodes)
    const topY = Math.min(posA.y, posB.y) - offset;
    rawCandidates.push([
      { x: cA.x + anchorOffset, y: posA.y },
      { x: cA.x + anchorOffset, y: topY },
      { x: cB.x + anchorOffset * 0.5, y: topY },
      { x: cB.x + anchorOffset * 0.5, y: posB.y },
    ]);

    // Bottom arch bypass (below both horizontal nodes)
    const bottomY = Math.max(posA.y + hA, posB.y + hB) + offset;
    rawCandidates.push([
      { x: cA.x + anchorOffset, y: posA.y + hA },
      { x: cA.x + anchorOffset, y: bottomY },
      { x: cB.x + anchorOffset * 0.5, y: bottomY },
      { x: cB.x + anchorOffset * 0.5, y: posB.y + hB },
    ]);
  } else {
    if (Math.abs(cA.x - cB.x) < 15) {
      if (dy > 0) {
        rawCandidates.push([
          { x: cA.x + anchorOffset, y: posA.y + hA },
          { x: cA.x + anchorOffset, y: posB.y },
        ]);
      } else {
        rawCandidates.push([
          { x: cA.x + anchorOffset, y: posA.y },
          { x: cA.x + anchorOffset, y: posB.y + hB },
        ]);
      }
    }

    if (dy > 0) {
      // Node A is on top, Node B is on bottom
      const midY = posA.y + hA + (posB.y - (posA.y + hA)) / 2;
      rawCandidates.push([
        { x: cA.x + anchorOffset, y: posA.y + hA },
        { x: cA.x + anchorOffset, y: midY },
        { x: cB.x + anchorOffset * 0.5, y: midY },
        { x: cB.x + anchorOffset * 0.5, y: posB.y },
      ]);
    } else {
      // Node B is on top, Node A is on bottom
      const midY = posB.y + hB + (posA.y - (posB.y + hB)) / 2;
      rawCandidates.push([
        { x: cA.x + anchorOffset, y: posA.y },
        { x: cA.x + anchorOffset, y: midY },
        { x: cB.x + anchorOffset * 0.5, y: midY },
        { x: cB.x + anchorOffset * 0.5, y: posB.y + hB },
      ]);
    }

    // Right bypass (to the right of both vertical nodes)
    const rightX = Math.max(posA.x + wA, posB.x + wB) + offset;
    rawCandidates.push([
      { x: posA.x + wA, y: cA.y + anchorOffset },
      { x: rightX, y: cA.y + anchorOffset },
      { x: rightX, y: cB.y + anchorOffset * 0.5 },
      { x: posB.x + wB, y: cB.y + anchorOffset * 0.5 },
    ]);

    // Left bypass (to the left of both vertical nodes)
    const leftX = Math.min(posA.x, posB.x) - offset;
    rawCandidates.push([
      { x: posA.x, y: cA.y + anchorOffset },
      { x: leftX, y: cA.y + anchorOffset },
      { x: leftX, y: cB.y + anchorOffset * 0.5 },
      { x: posB.x, y: cB.y + anchorOffset * 0.5 },
    ]);
  }

  // 2. Perimeter clearance paths
  const srcPorts = [
    {
      side: "right",
      start: { x: posA.x + wA, y: cA.y + anchorOffset },
      lane: { x: rightLane, y: cA.y + anchorOffset },
    },
    {
      side: "left",
      start: { x: posA.x, y: cA.y + anchorOffset },
      lane: { x: leftLane, y: cA.y + anchorOffset },
    },
    {
      side: "top",
      start: { x: cA.x + anchorOffset, y: posA.y },
      lane: { x: cA.x + anchorOffset, y: topLane },
    },
    {
      side: "bottom",
      start: { x: cA.x + anchorOffset, y: posA.y + hA },
      lane: { x: cA.x + anchorOffset, y: bottomLane },
    },
  ];

  const tgtPorts = [
    {
      side: "right",
      start: { x: posB.x + wB, y: cB.y + anchorOffset * 0.5 },
      lane: { x: rightLane, y: cB.y + anchorOffset * 0.5 },
    },
    {
      side: "left",
      start: { x: posB.x, y: cB.y + anchorOffset * 0.5 },
      lane: { x: leftLane, y: cB.y + anchorOffset * 0.5 },
    },
    {
      side: "top",
      start: { x: cB.x + anchorOffset * 0.5, y: posB.y },
      lane: { x: cB.x + anchorOffset * 0.5, y: topLane },
    },
    {
      side: "bottom",
      start: { x: cB.x + anchorOffset * 0.5, y: posB.y + hB },
      lane: { x: cB.x + anchorOffset * 0.5, y: bottomLane },
    },
  ];

  const corners = {
    topLeft: { x: leftLane, y: topLane },
    topRight: { x: rightLane, y: topLane },
    bottomLeft: { x: leftLane, y: bottomLane },
    bottomRight: { x: rightLane, y: bottomLane },
  };

  for (const sPort of srcPorts) {
    for (const tPort of tgtPorts) {
      if (sPort.side === tPort.side) {
        rawCandidates.push([sPort.start, sPort.lane, tPort.lane, tPort.start]);
      } else if (
        (sPort.side === "left" && tPort.side === "top") ||
        (sPort.side === "top" && tPort.side === "left")
      ) {
        rawCandidates.push([sPort.start, sPort.lane, corners.topLeft, tPort.lane, tPort.start]);
      } else if (
        (sPort.side === "right" && tPort.side === "top") ||
        (sPort.side === "top" && tPort.side === "right")
      ) {
        rawCandidates.push([sPort.start, sPort.lane, corners.topRight, tPort.lane, tPort.start]);
      } else if (
        (sPort.side === "left" && tPort.side === "bottom") ||
        (sPort.side === "bottom" && tPort.side === "left")
      ) {
        rawCandidates.push([sPort.start, sPort.lane, corners.bottomLeft, tPort.lane, tPort.start]);
      } else if (
        (sPort.side === "right" && tPort.side === "bottom") ||
        (sPort.side === "bottom" && tPort.side === "right")
      ) {
        rawCandidates.push([sPort.start, sPort.lane, corners.bottomRight, tPort.lane, tPort.start]);
      } else if (sPort.side === "left" && tPort.side === "right") {
        rawCandidates.push([sPort.start, sPort.lane, corners.topLeft, corners.topRight, tPort.lane, tPort.start]);
        rawCandidates.push([sPort.start, sPort.lane, corners.bottomLeft, corners.bottomRight, tPort.lane, tPort.start]);
      } else if (sPort.side === "right" && tPort.side === "left") {
        rawCandidates.push([sPort.start, sPort.lane, corners.topRight, corners.topLeft, tPort.lane, tPort.start]);
        rawCandidates.push([sPort.start, sPort.lane, corners.bottomRight, corners.bottomLeft, tPort.lane, tPort.start]);
      } else if (sPort.side === "top" && tPort.side === "bottom") {
        rawCandidates.push([sPort.start, sPort.lane, corners.topLeft, corners.bottomLeft, tPort.lane, tPort.start]);
        rawCandidates.push([sPort.start, sPort.lane, corners.topRight, corners.bottomRight, tPort.lane, tPort.start]);
      } else if (sPort.side === "bottom" && tPort.side === "top") {
        rawCandidates.push([sPort.start, sPort.lane, corners.bottomLeft, corners.topLeft, tPort.lane, tPort.start]);
        rawCandidates.push([sPort.start, sPort.lane, corners.bottomRight, corners.topRight, tPort.lane, tPort.start]);
      }
    }
  }

  let bestPath = null;
  let minCol = Infinity;
  let minLen = Infinity;

  for (const raw of rawCandidates) {
    const pts = cleanPoints(raw);
    const col = countCollisions(pts, obstacles, srcBox, tgtBox);
    const len = pathLength(pts);

    if (col < minCol) {
      minCol = col;
      minLen = len;
      bestPath = pts;
    } else if (col === minCol && len < minLen) {
      minLen = len;
      bestPath = pts;
    }
  }

  return bestPath ? buildSmoothOrthogonalPath(bestPath, 16) : "";
};

const SwmLinkEdge = ({ id, source, target, style, markerEnd, data }) => {
  const sourceNode = useInternalNode(source);
  const targetNode = useInternalNode(target);
  const allNodes = useNodes();
  const linkIndex = data?.linkIndex || 0;
  const totalLinks = data?.totalLinks || 1;

  const edgePath = getPerimeterEdgePath(
    sourceNode,
    targetNode,
    allNodes,
    linkIndex,
    totalLinks,
  );

  if (!edgePath) return null;

  const strokeWidth = Number(style?.strokeWidth) || 3.5;

  return (
    <g className="react-flow__edge-swm-link" style={{ pointerEvents: "none" }}>
      {/* Halo / casing underlay: acts as a clean visual bridge over crossing lines */}
      <path
        d={edgePath}
        fill="none"
        stroke="#ffffff"
        strokeWidth={strokeWidth + 4}
        strokeLinecap="round"
        strokeLinejoin="round"
        style={{
          strokeDasharray: "none",
          animation: "none",
          opacity: 0.95,
        }}
      />
      {/* Active colored edge path */}
      <BaseEdge
        id={id}
        path={edgePath}
        style={{
          ...style,
          strokeWidth,
        }}
        markerEnd={markerEnd}
      />
    </g>
  );
};

SwmLinkEdge.propTypes = {
  id: PropTypes.string,
  source: PropTypes.string,
  target: PropTypes.string,
  style: PropTypes.object,
  markerEnd: PropTypes.any,
  data: PropTypes.shape({
    linkIndex: PropTypes.number,
    totalLinks: PropTypes.number,
    color: PropTypes.string,
  }),
};

const EDGE_TYPES = {
  swmLinkEdge: SwmLinkEdge,
};

const getNodeColor = (nodeId, selectedNodeId, linkedSwmIds) => {
  customConsole("nodeId, selectedNodeId, linkedSwmIds");
  customConsole({ nodeId, selectedNodeId, linkedSwmIds });
  if (nodeId === selectedNodeId) return "#00FF1A";
  if (
    selectedNodeId &&
    linkedSwmIds?.includes(Number(selectedNodeId?.replace("swm-", "")))
  )
    return "gold";
  return "#2897DC";
};

const createHierarchyData = (projectDetails) => ({
  id: `project-${projectDetails.project_id}`,
  name: projectDetails.name,
  children: projectDetails.ee_details.map((ee) => ({
    id: `ee-${ee.ee_id}`,
    name: ee.description,
    version: ee.version,
    children: projectDetails.swm_details
      .filter((swm) => swm.ee_id === ee.ee_id)
      .map((swm) => ({
        id: `swm-${swm.swm_id}`,
        name: swm.name,
        version: swm.version,
        linkedSwmIds: swm.linked_swm_ids,
      })),
  })),
});

const createNodesAndEdges = (hierarchyData, selectedNodeId, mindMapProjectDetails) => {
  const nodes = [];
  const edges = [];

  const root = d3.hierarchy(hierarchyData);
  const treeLayout = d3
    .tree()
    .nodeSize([nodeWidth + marginX, nodeHeight + marginY]);
  treeLayout(root);

  for (const d of root.descendants()) {
    let nodeShape, nodeStyle;

    if (d.data.id.startsWith("project-")) {
      nodeShape = (
        <div style={{ width: "100%", padding: "6px", boxSizing: "border-box" }}>
          <div
            style={{
              ...CLAMPED_TEXT_BASE_STYLE,
              fontSize: "11px",
              fontWeight: "700",
              WebkitLineClamp: 2,
              maxHeight: "24px",
            }}
          >
            {d.data.name}
          </div>
        </div>
      );
      nodeStyle = {
        background: "black",
        color: "white",
        width: nodeWidth,
        height: nodeHeight,
        borderRadius: "50%", // Circle shape
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        overflow: "hidden",
      };
    } else if (d.data.id.startsWith("ee-")) {
      nodeShape = (
        <div style={{ width: "100%", padding: "6px", boxSizing: "border-box" }}>
          <div
            style={{
              ...CLAMPED_TEXT_BASE_STYLE,
              fontSize: "11px",
              fontWeight: "700",
              WebkitLineClamp: 2,
              maxHeight: "24px",
            }}
          >
            {d.data.name}
          </div>
          {d.data.version ? (
            <div
              style={{
                ...CLAMPED_TEXT_BASE_STYLE,
                fontSize: "10px",
                fontWeight: "400",
                WebkitLineClamp: 1,
                maxHeight: "12px",
              }}
            >
              Version: {d.data.version}
            </div>
          ) : null}
        </div>
      );
      nodeStyle = {
        background: d.data.id === selectedNodeId ? "#D0D0CE" : "black",
        color: d.data.id === selectedNodeId ? "black" : "white",
        width: nodeWidth,
        height: nodeHeight,
        borderRadius: "50%", // Circle shape
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        overflow: "hidden",
      };
    } else if (d.data.id.startsWith("swm-")) {
      nodeShape = (
        <div style={{ width: "100%", padding: "6px", boxSizing: "border-box" }}>
          <div
            style={{
              ...CLAMPED_TEXT_BASE_STYLE,
              fontSize: "11px",
              fontWeight: "700",
              WebkitLineClamp: 2,
              maxHeight: "24px",
            }}
          >
            {d.data.name}
          </div>
          {d.data.version ? (
            <div
              style={{
                ...CLAMPED_TEXT_BASE_STYLE,
                fontSize: "10px",
                fontWeight: "400",
                WebkitLineClamp: 1,
                maxHeight: "12px",
              }}
            >
              Version: {d.data.version}
            </div>
          ) : null}
        </div>
      );
      nodeStyle = {
        background: getNodeColor(
          d.data.id,
          selectedNodeId,
          d.data.linkedSwmIds,
        ),
        color: "white",
        width: nodeWidth,
        height: nodeHeight,
        borderRadius: "0%",
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        overflow: "hidden",
      };
    }

    nodes.push({
      id: d.data.id,
      data: { label: nodeShape },
      position: { x: d.x, y: d.y },
      style: nodeStyle,
    });

    if (d.parent) {
      edges.push({
        id: `${d.parent.data.id}-${d.data.id}`,
        source: d.parent.data.id,
        target: d.data.id,
        type: "step",
        animated: false,
        markerEnd: {
          type: MarkerType.ArrowClosed,
          color: "#000000",
        },
      });
    }
  }

  // If a software module is selected, add link edges to all its linked software modules
  if (
    selectedNodeId?.startsWith("swm-") &&
    Array.isArray(mindMapProjectDetails?.swm_details)
  ) {
    const selSwmNum = Number(selectedNodeId.replace("swm-", ""));
    const selSwm = mindMapProjectDetails.swm_details.find(
      (s) => Number(s.swm_id) === selSwmNum,
    );

    if (Array.isArray(selSwm?.linked_swm_ids)) {
      selSwm.linked_swm_ids.forEach((linkedObj, idx) => {
        const targetSwmId = `swm-${linkedObj.id}`;
        const targetColor = linkedObj.color || "#000000";

        edges.push({
          id: `swm-link-${selectedNodeId}-${targetSwmId}`,
          source: selectedNodeId,
          target: targetSwmId,
          type: "swmLinkEdge",
          animated: true,
          zIndex: 1000 + idx,
          data: {
            linkIndex: idx,
            totalLinks: selSwm.linked_swm_ids.length,
            color: targetColor,
          },
          style: {
            stroke: targetColor,
            strokeWidth: 3.5,
          },
        });
      });
    }
  }

  return { nodes, edges };
};

function VlcZonalMindMap({ selectedProjectId, selectedVariantId, filters, onResetFilters }) {
  const dispatch = useDispatch();

  const { projectDetailsMindMap } = useSelector(
    (state) => state.labCarReducer,
    shallowEqual,
  );

  const [mindMapProjectDetails, setMindMapProjectDetails] = useState({});

  const [flowNodes, setFlowNodes] = useState([]);
  const [flowEdges, setFlowEdges] = useState([]);
  const [selectedNode, setSelectedNode] = useState(null);
  const rfInstanceRef = useRef(null);

  const handleReset = useCallback(() => {
    setSelectedNode(null);
    onResetFilters?.();
    setTimeout(() => {
      rfInstanceRef.current?.fitView({ duration: 300 });
    }, 50);
  }, [onResetFilters]);

  useEffect(() => {
    if (selectedProjectId && selectedVariantId) {
      dispatch(
        HandleApiActions({
          ...labCar.get_project_details_mind_map,
          params: {
            project_id: selectedProjectId,
            variant_id: selectedVariantId,
          },
          show_toast: false,
        }),
      );
    }
  }, [dispatch, selectedProjectId, selectedVariantId]);

  useEffect(() => {
    if (projectDetailsMindMap?.ee_details) {
      setMindMapProjectDetails(projectDetailsMindMap);
    }
  }, [projectDetailsMindMap]);

  useEffect(() => {
    if (mindMapProjectDetails.ee_details) {
      const hierarchyData = createHierarchyData(mindMapProjectDetails);
      const { nodes, edges } = createNodesAndEdges(
        hierarchyData,
        selectedNode,
        mindMapProjectDetails,
      );
      setFlowNodes(nodes);
      setFlowEdges(edges);
    }
  }, [mindMapProjectDetails, selectedNode]);

  const onNodesChange = useCallback(
    (changes) => setFlowNodes((nds) => applyNodeChanges(changes, nds)),
    [],
  );

  const onEdgesChange = useCallback(
    (changes) => setFlowEdges((eds) => applyEdgeChanges(changes, eds)),
    [],
  );

  const onConnect = useCallback(
    (params) => setFlowEdges((eds) => addEdge(params, eds)),
    [],
  );

  const handleNodeClick = useCallback((event, node) => {
    setSelectedNode(node.id);
  }, []);

  const animatedEdges = useMemo(() => {
    return flowEdges.map((edge) => {
      if (edge.type === "swmLinkEdge") {
        return edge;
      }
      return {
        ...edge,
        style: {
          strokeWidth: 2, // Increase this value to make the line thicker
          stroke: "#000000",
        },
      };
    });
  }, [flowEdges]);

  return (
    <Box sx={styles.mainContainer}>
      <ReactFlow
        nodes={flowNodes}
        edges={animatedEdges}
        edgeTypes={EDGE_TYPES}
        onNodesChange={onNodesChange}
        onEdgesChange={onEdgesChange}
        onConnect={onConnect}
        onNodeClick={handleNodeClick}
        onInit={(instance) => (rfInstanceRef.current = instance)}
        fitView
      >
        <Background />
        <Controls>
          <ControlButton onClick={handleReset} title="Reset" aria-label="Reset">
            <RestartAltIcon sx={{ fontSize: "14px" }} />
          </ControlButton>
        </Controls>
      </ReactFlow>
    </Box>
  );
}

VlcZonalMindMap.propTypes = {
  selectedProjectId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  selectedVariantId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
};

export default VlcZonalMindMap;

const styles = {
  mainContainer: {
    height: "520px",
    width: "100%",
    backgroundColor: "#F5F5F5",
    border: "1px solid grey",
    borderRadius: 1,
    padding: "20px",
    mt: 1,
    boxShadow: 4,
  },
};
